import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllServicemen,
  toggleVerification,
  deleteServicemen,
  clearServicemenMessages,
} from "../redux/slices/servicemenSlice";

import Swal from "sweetalert2";
import { Link } from "react-router-dom";

const NewServicemen = () => {
  const dispatch = useDispatch();

  const { allServicemen, loading, successMessage, errorMessage, pagination } =
    useSelector((state) => state.servicemen);

  // -------------------------------
  // 🔍 Search + Tabs State
  // -------------------------------
  const [searchText, setSearchText] = useState("");
  const [activeTab, setActiveTab] = useState("all"); // replaced radio state

  // Fetch servicemen
  useEffect(() => {
    dispatch(getAllServicemen({ page: 1, limit: 20 }));
  }, [dispatch]);

  // Alerts
  useEffect(() => {
    if (successMessage) {
      Swal.fire("Success", successMessage, "success");
      dispatch(clearServicemenMessages());
    }
    if (errorMessage) {
      Swal.fire("Error", errorMessage, "error");
      dispatch(clearServicemenMessages());
    }
  }, [successMessage, errorMessage, dispatch]);

  // Approve / Reject
  const handleVerify = (id) => {
    dispatch(toggleVerification(id));
  };

  // Delete
  const handleDelete = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This servicemen profile will be deleted permanently!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(deleteServicemen(id));
      }
    });
  };

  // -------------------------------
  // 🔎 Filter Logic (Frontend)
  // -------------------------------

  const filteredServicemen = allServicemen?.filter((x) => {
    // Search by name / number
    const matchesSearch =
      x.user?.name?.toLowerCase().includes(searchText.toLowerCase()) ||
      x.contactNumber?.toString().includes(searchText);

    // Tabs filter
    const matchesStatus =
      activeTab === "all"
        ? true
        : activeTab === "verified"
          ? x.verified === true
          : x.verified === false;

    return matchesSearch && matchesStatus;
  });

  return (
    <div className="container mt-4">
      <div className="card">
        <div className="card-header text-center">
          <h5 className="my-2">All Servicemen</h5>
        </div>

        {/* -------------------------------
            🔍 Search + Tabs
        ------------------------------- */}
        <div className="p-3 d-flex justify-content-between align-items-center">
          {/* Search */}
          <input
            type="text"
            className="form-control w-50"
            placeholder="Search by name or contact number..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>

        {/* ---------- Bootstrap Tabs ---------- */}
        <ul className="nav border-none nav-tabs px-3 my-3 d-flex gap-3 mb-4" style={{ border: 0 }}>
          <li className="nav-item">
            <button
              style={{
                padding: '8px 16px',
                borderRadius: 8,
                border: 'none',
                cursor: 'pointer',
                backgroundColor: activeTab === "all" ? '#f41b3b' : '#efefef',
                color: activeTab === "all" ? '#fff' : '#000'
              }}
              onClick={() => setActiveTab("all")}
            >
              All
            </button>
          </li>

          <li className="nav-item">
            <button
              onClick={() => setActiveTab("verified")}
              style={{
                padding: '8px 16px',
                borderRadius: 8,
                border: 'none',
                cursor: 'pointer',
                backgroundColor: activeTab === "verified" ? '#f41b3b' : '#efefef',
                color: activeTab === "verified" ? '#fff' : '#000'
              }}
            >
              Verified
            </button>
          </li>

          <li className="nav-item">
            <button
              style={{
                padding: '8px 16px',
                borderRadius: 8,
                border: 'none',
                cursor: 'pointer',
                backgroundColor: activeTab === "unverified" ? '#f41b3b' : '#efefef',
                color: activeTab === "unverified" ? '#fff' : '#000'
              }}
              onClick={() => setActiveTab("unverified")}
            >
              Unverified
            </button>
          </li>
        </ul>

        {loading && <p>Loading...</p>}

        {/* TABLE */}
        <div className="table-responsive mt-3">
          <table className="table table-bordered table-striped align-middle">
            <thead className="table-dark">
              <tr>
                <th>#</th>
                <th>Profile</th>
                <th>Name</th>
                <th>Phone</th>
                <th>Service</th>
                <th>Location</th>
                <th>Status</th>
                <th>Leads</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>

            <tbody>
              {filteredServicemen?.length > 0 ? (
                filteredServicemen.map((item, index) => (
                  <tr key={item._id}>
                    <td>{index + 1}</td>

                    <td>
                      <img
                        src={item.profileImage}
                        alt="Profile"
                        style={{
                          width: "60px",
                          height: "60px",
                          objectFit: "cover",
                          borderRadius: "6px",
                        }}
                      />
                    </td>

                    <td>{item.user?.name}</td>
                    <td>{item.contactNumber}</td>
                    <td>{item.category?.name || "N/A"}</td>
                    <td>
                      {item.city || "N/A"}, {item.state || "N/A"},{" "}
                      {item.address || "N/A"}
                    </td>

                    <td>
                      {item.verified === true ? (
                        <span className="badge bg-success">Approved</span>
                      ) : (
                        <span className="badge bg-warning text-dark">
                          Pending
                        </span>
                      )}
                    </td>

                    <td className="text-center">
                      <Link
                        to={`/servicemen-leads/${item._id}`}
                        className="btn py-2 btn-sm btn-primary"
                      >
                        Leads
                      </Link>
                    </td>

                    {/* ACTION BUTTONS */}
                    <td className="text-center">
                      <Link
                        to={`/servicemen/${item.user?._id}`}
                        className="btn btn-sm btn-primary"
                      >
                        View
                      </Link>

                      <Link
                        to={`/edit-servicemen/${item._id}`}
                        className="btn btn-sm btn-info mx-1"
                      >
                        Edit
                      </Link>

                      {item.verified === false && (
                        <button
                          className={`btn btn-sm ${item.isVerified ? "btn-danger" : "btn-success"} me-2`}
                          onClick={() => handleVerify(item._id)}
                        >
                          {item.isVerified ? "Reject" : "Approve"}
                        </button>
                      )}

                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDelete(item._id)}
                      >
                        Delete
                      </button>

                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-center">
                    No servicemen found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {pagination && (
        <div className="d-flex justify-content-center mt-3">
          <button
            disabled={pagination.currentPage === 1}
            className="btn btn-secondary me-2"
            onClick={() =>
              dispatch(
                getAllServicemen({
                  page: pagination.currentPage - 1,
                  limit: 20,
                })
              )
            }
          >
            Previous
          </button>

          <button
            disabled={pagination.currentPage === pagination.totalPages}
            className="btn btn-secondary"
            onClick={() =>
              dispatch(
                getAllServicemen({
                  page: pagination.currentPage + 1,
                  limit: 20,
                })
              )
            }
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default NewServicemen;
